import { useId } from "react";

import { FieldList, Panel } from "./ProfilePanels.tsx";
import { PROFILE_COPY } from "./profile.constants.ts";
import type { ContactValues, Field } from "./profile.model.ts";

/**
 * What ONDO holds, and the three fields the account holder may correct.
 *
 * Email is shown and never editable: it is the auth identity, changed through
 * the sign-in provider rather than through the registry, and a box here that
 * silently did nothing would be worse than no box.
 *
 * Phone and country are NOT required. Under the replace semantics
 * `ondo.update_person()` has, clearing a field is how you withdraw a fact — so
 * marking either required would make the two facts impossible to take back.
 */
export function ProfileContact({
  fields,
  draft,
  busy,
  saved,
  error,
  onEdit,
  onCancel,
  onChange,
  onSave,
}: {
  fields: Field[];
  /** Null when the panel is read-only. */
  draft: ContactValues | null;
  busy: boolean;
  saved: boolean;
  error: string | null;
  onEdit: () => void;
  onCancel: () => void;
  onChange: (patch: Partial<ContactValues>) => void;
  onSave: () => void;
}) {
  const nameId = useId();
  const phoneId = useId();
  const countryId = useId();

  if (!draft) {
    return (
      <Panel
        title={PROFILE_COPY.contactHeading}
        note={PROFILE_COPY.contactNote}
        action={
          <button type="button" className="profile__action" onClick={onEdit}>
            {PROFILE_COPY.edit}
          </button>
        }
      >
        <FieldList fields={fields} />
        <p className="profile__hint">{PROFILE_COPY.emailNote}</p>
        {saved && (
          <p className="profile__saved" role="status">
            {PROFILE_COPY.saved}
          </p>
        )}
      </Panel>
    );
  }

  return (
    <Panel title={PROFILE_COPY.contactHeading} note={PROFILE_COPY.contactNote}>
      <form
        className="profile__form"
        onSubmit={(event) => {
          event.preventDefault();
          onSave();
        }}
      >
        <label className="profile__label" htmlFor={nameId}>
          {PROFILE_COPY.nameLabel}
        </label>
        <input
          id={nameId}
          className="profile__input"
          value={draft.displayName}
          maxLength={120}
          onChange={(event) => onChange({ displayName: event.target.value })}
        />

        <label className="profile__label" htmlFor={phoneId}>
          {PROFILE_COPY.phoneLabel}
        </label>
        <input
          id={phoneId}
          className="profile__input"
          value={draft.phone}
          maxLength={40}
          onChange={(event) => onChange({ phone: event.target.value })}
        />

        <label className="profile__label" htmlFor={countryId}>
          {PROFILE_COPY.countryLabel}
        </label>
        <input
          id={countryId}
          className="profile__input"
          value={draft.country}
          maxLength={80}
          onChange={(event) => onChange({ country: event.target.value })}
        />

        <p className="profile__hint">{PROFILE_COPY.emailNote}</p>

        {error && (
          <p className="profile__error" role="alert">
            {error}
          </p>
        )}

        <div className="profile__form-actions">
          <button type="submit" className="profile__primary" disabled={busy}>
            {busy ? PROFILE_COPY.saving : PROFILE_COPY.save}
          </button>
          <button
            type="button"
            className="profile__action"
            onClick={onCancel}
            disabled={busy}
          >
            {PROFILE_COPY.cancel}
          </button>
        </div>
      </form>
    </Panel>
  );
}
